import React, { useState } from 'react';
import { Play, Mic, ChevronLeft, ChevronRight } from 'lucide-react';
import MediaLightbox from './MediaLightbox';

function MediaGallery({ media = [], currentIndex = 0, onIndexChange }) {
  const [showModal, setShowModal] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  // Check if media type is audio (either 'audio' type or video with audio filename)
  const isAudioMedia = (item) => {
    return item.type === 'audio' ||
      (item.type === 'video' && item.url?.includes('audio'));
  };

  if (media.length === 0) return null;

  const mainMedia = media[currentIndex] || media[0];
  const thumbnails = media.slice(0, 6);
  const remainingCount = media.length - 6;

  const handleThumbnailClick = (index) => {
    onIndexChange?.(index);
  };

  const handlePrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === 0 ? media.length - 1 : currentIndex - 1;
    onIndexChange?.(newIndex);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    const newIndex = currentIndex === media.length - 1 ? 0 : currentIndex + 1;
    onIndexChange?.(newIndex);
  };

  const handleMainClick = () => {
    setModalIndex(currentIndex);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <div className="media-gallery">
      {/* Main Photo */}
      <div className="media-gallery-main" onClick={handleMainClick}>
        {isAudioMedia(mainMedia) ? (
          <div className="hero-audio">
            <div className="audio-player-card">
              <div className="audio-icon">
                <Mic size={32} />
              </div>
              <p className="audio-label">Voice Recording</p>
              <p className="audio-hint">Click to play</p>
            </div>
          </div>
        ) : mainMedia.type === 'video' ? (
          <div className="media-gallery-video">
            <video src={`${mainMedia.url}#t=0.5`} muted playsInline preload="auto" />
            <div className="video-play-overlay">
              <div className="video-play-button">
                <Play size={20} fill="#333" />
              </div>
            </div>
          </div>
        ) : (
          <img
            src={mainMedia.url}
            alt={`Media ${currentIndex + 1}`}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e0e0e0" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%23999" font-size="12">No Image</text></svg>';
            }}
          />
        )}
        <div className="photo-count-badge">
          {currentIndex + 1} / {media.length}
        </div>

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            <button
              className="media-nav-button media-nav-prev"
              onClick={handlePrevious}
              aria-label="Previous media"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              className="media-nav-button media-nav-next"
              onClick={handleNext}
              aria-label="Next media"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Thumbnail Grid */}
      <div className="media-gallery-thumbs">
        {thumbnails.map((item, index) => (
          <div
            key={item.id || index}
            className={`media-gallery-thumb ${index === currentIndex ? 'active' : ''}`}
            onClick={() => handleThumbnailClick(index)}
          >
            {isAudioMedia(item) ? (
              <div className="thumb-audio">
                <Mic size={16} />
              </div>
            ) : item.type === 'video' ? (
              <div className="thumb-video">
                <video src={`${item.url}#t=0.5`} muted playsInline preload="auto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div className="thumb-video-overlay small">
                  <Play size={12} fill="#fff" />
                </div>
              </div>
            ) : (
              <img
                src={item.thumbnailUrl || item.url}
                alt={`Thumbnail ${index + 1}`}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e0e0e0" width="100" height="100"/></svg>';
                }}
              />
            )}
          </div>
        ))}
        {remainingCount > 0 && (
          <div
            className="media-gallery-thumb more-count"
            onClick={() => onIndexChange?.(6)}
            title="View more media"
          >
            <span>+{remainingCount}</span>
          </div>
        )}
      </div>

      {/* Full-screen Lightbox (TimeCapsule style) */}
      {showModal && (
        <MediaLightbox
          media={media}
          currentIndex={modalIndex}
          onIndexChange={setModalIndex}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

export default MediaGallery;
